/**
 * QHS PatientBanner
 * -----------------
 * Sub-page identity strip used on every page that lives within the
 * context of a single patient (assessment report, edit order, claim
 * editor, etc.). Sits just below the breadcrumb / page header.
 *
 * Renders the patient avatar, primary identity (last, first), age and
 * gender, MRN/DOB meta, and an optional "Open patient dashboard"
 * action. The drug-allergy chip is shown only when the backend reports
 * `hasDrugAllergies === true` — the patient API does not return
 * individual allergen names, so the chip is generic.
 *
 * Three render branches:
 *   - loading without a payload yet: thin skeleton row so the layout
 *     doesn't pop in / out as the patient query resolves
 *   - anonymous patient: minimal strip with a generic title and the
 *     account business name (DS only) — no MRN/DOB/age
 *   - regular patient: full banner.
 *
 * 40px avatar comes from shared Avatar (size 40).
 *
 * The component is presentational. Caller owns the navigation handler
 * (`onOpenDashboard`); banner stays decoupled from routing.
 *
 *   <PatientBanner
 *     patient={patient}
 *     loading={loading}
 *     labels={{ openDashboard: t.openPatientDashboard, drugAllergies: t.drugAllergies, anonymousPatient: t.anonymousPatient }}
 *     onOpenDashboard={() => navigate(...)}
 *   />
 */
import { useSelector } from 'react-redux';
import { styled } from '../styled';
import { Button } from '../Button';
import Avatar from '../Avatar/Avatar';
import { getAgeGenderLine } from '@shared/lib/patientDisplay';

const Banner = styled('div')({
  margin: '12px 24px 0',
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 14,
  padding: '12px 18px',
  display: 'grid',
  gridTemplateColumns: '40px 1fr auto',
  gap: 14,
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  // Guard against parents (flex columns, sticky headers) squashing
  // the banner when content below is still loading. Without these the
  // banner can render as a 0-pixel strip on pages whose first paint
  // happens before the form fills the viewport.
  flexShrink: 0,
  minHeight: 64,
  boxSizing: 'border-box',
});

const AvatarSkeleton = styled('div')({
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'var(--qhs-surface-hover)',
});

const Name = styled('div')({
  fontFamily: 'var(--qhs-font-head)',
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
});

const Age = styled('span')({
  fontFamily: 'var(--qhs-font-body)',
  fontSize: 12,
  fontWeight: 400,
  color: 'var(--qhs-text-2)',
});

const Meta = styled('div')({
  marginTop: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 12,
  color: 'var(--qhs-text-2)',
  flexWrap: 'wrap',
  '& b': { color: 'var(--qhs-text-1)', fontWeight: 500 },
  '& .mono': { fontFamily: 'var(--qhs-font-mono)' },
});

const Sep = styled('span')({
  width: 3,
  height: 3,
  borderRadius: '50%',
  background: 'var(--qhs-text-4)',
  flexShrink: 0,
});

const ContextRow = styled('div')({
  marginTop: 8,
  paddingTop: 8,
  borderTop: '1px solid var(--qhs-border)',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  fontSize: 12,
  color: 'var(--qhs-text-2)',
  flexWrap: 'wrap',
});

const ContextBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--qhs-brand-tint, rgba(0, 168, 255, 0.1))',
  color: 'var(--qhs-brand, #00A8FF)',
  fontWeight: 700,
  fontSize: 10.5,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  flexShrink: 0,
});

const ContextName = styled('span')({
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
  fontSize: 13,
});

const ContextGroup = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginRight: 8,
});

const ContextMeta = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  color: 'var(--qhs-text-2)',
  '& b': { color: 'var(--qhs-text-1)', fontWeight: 500 },
  '& svg': { width: 12, height: 12, opacity: 0.7 },
});

const CalendarIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CheckIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AllergyChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--qhs-st-critical-bg)',
  color: 'var(--qhs-st-critical-tx)',
  fontWeight: 600,
  fontSize: 11,
  '& svg': { width: 11, height: 11 },
});

const SkeletonRow = styled('div')({
  height: 12,
  borderRadius: 4,
  background: 'var(--qhs-surface-hover)',
});

const SkeletonRowSm = styled('div')({
  height: 8,
  borderRadius: 4,
  background: 'var(--qhs-surface-hover)',
  marginTop: 8,
  width: '60%',
});

const getInitials = (firstName = '', lastName = '') => {
  const first = (firstName || '').trim().charAt(0);
  const last = (lastName || '').trim().charAt(0);
  return `${last}${first}`.toUpperCase() || '??';
};

const ArrowRight = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const AllergyIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const DEFAULT_LABELS = {
  mrn: 'MRN',
  dob: 'DOB',
  openDashboard: 'Open patient dashboard',
  drugAllergies: 'Drug Allergies',
  anonymousPatient: 'Anonymous patient',
};

/**
 * Optional `context` prop shape:
 *   {
 *     badge?: string,   // small colored pill (e.g. "PROGRAM", "ASSESSMENT")
 *     name?: string,    // primary label next to the badge
 *     meta?: Array<{ label: string, value: string }>,
 *   }
 * When set, renders an extra row underneath the patient identity with
 * the page-specific context (e.g. Assessment name + completion dates
 * on the Edit Claim page). Meta rows without a `value` are skipped.
 * Common meta labels ("Ordered", "Completed") auto-pick a matching
 * inline icon so the caller stays lean.
 */
const iconForLabel = label => {
  const l = (label || '').toLowerCase();
  if (l.includes('complet')) return CheckIcon;
  return CalendarIcon;
};

const PatientBanner = ({ patient, loading, labels, onOpenDashboard, context }) => {
  // Catalog labels sit between the English defaults and any explicit
  // `labels` prop overrides from the caller.
  const i18nCatalog = useSelector(state => state.i18n) || {};
  const t = { ...DEFAULT_LABELS, ...(i18nCatalog.patientHero || {}), ...(labels || {}) };

  // Loading skeleton — keeps the slot reserved so the rest of the
  // page doesn't reflow when the patient payload arrives.
  if (!patient && loading) {
    return (
      <Banner>
        <AvatarSkeleton />
        <div>
          <SkeletonRow style={{ width: 200 }} />
          <SkeletonRowSm />
        </div>
        <span />
      </Banner>
    );
  }

  if (!patient) return null;

  const {
    firstName,
    lastName,
    age,
    gender,
    mrn,
    dob,
    hasDrugAllergies,
    isAnonymousPatient,
    accountBusinessName,
  } = patient;

  // Anonymous patients have no MRN / DOB / name to surface — render
  // a minimal strip so the layout stays consistent without leaking
  // empty fields.
  if (isAnonymousPatient) {
    return (
      <Banner>
        <Avatar size="40" initials="--" />
        <div>
          <Name>{t.anonymousPatient}</Name>
          {accountBusinessName && (
            <Meta>
              <span>
                <b>{accountBusinessName}</b>
              </span>
            </Meta>
          )}
        </div>
        {onOpenDashboard && (
          <Button variant="primary" size="sm" onClick={onOpenDashboard} endIcon={ArrowRight}>
            {t.openDashboard}
          </Button>
        )}
      </Banner>
    );
  }

  const ageGender = getAgeGenderLine({ age, gender }, i18nCatalog);

  return (
    <Banner>
      <Avatar size="40" initials={getInitials(firstName, lastName)} />
      <div>
        <Name>
          {[lastName, firstName].filter(Boolean).join(', ')}
          {ageGender && <Age>{ageGender}</Age>}
        </Name>
        <Meta>
          {mrn && (
            <span>
              {t.mrn} <b className="mono">{mrn}</b>
            </span>
          )}
          {mrn && dob && <Sep />}
          {dob && (
            <span>
              {t.dob} <b className="mono">{dob}</b>
            </span>
          )}
          {hasDrugAllergies && (mrn || dob) && <Sep />}
          {hasDrugAllergies && (
            <AllergyChip>
              {AllergyIcon}
              {t.drugAllergies}
            </AllergyChip>
          )}
        </Meta>
        {context &&
          (context.badge || context.name || (context.meta && context.meta.some(m => m?.value))) && (
            <ContextRow>
              {(context.badge || context.name) && (
                <ContextGroup>
                  {context.badge && <ContextBadge>{context.badge}</ContextBadge>}
                  {context.name && <ContextName>{context.name}</ContextName>}
                </ContextGroup>
              )}
              {(context.meta || [])
                .filter(m => m?.value)
                .map(m => (
                  <ContextMeta key={m.label}>
                    {iconForLabel(m.label)}
                    <span>
                      {m.label} <b>{m.value}</b>
                    </span>
                  </ContextMeta>
                ))}
            </ContextRow>
          )}
      </div>
      {onOpenDashboard && (
        <Button variant="primary" size="sm" onClick={onOpenDashboard} endIcon={ArrowRight}>
          {t.openDashboard}
        </Button>
      )}
    </Banner>
  );
};

export default PatientBanner;
